import React from 'react';
import theme from './ChangeRequestOverrideOptionSelect.module.scss';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Badge, Checkbox, List } from '@ygtechnologies/rubus-ui';
import { Plural, Trans } from '@lingui/react/macro';
import { classnames } from '@/utils/html';
import { useAsyncState, useGetActiveProjectId } from '@/utils/hooks';
import { LinkingType, OptionGroupDto, OptionGroupLinkStatus, OptionGroupOverrideSelection } from '@/types';
import { RoomPill } from '@/seller/components/options/Rooms/RoomPill';
import OptionGroupLabel from '@/seller/components/options/OptionGroupLabel/OptionGroupLabel';
import { searchOptionGroups } from '@/services/options/option-groups';
import { getRooms } from '@/services/rooms';
import { getProjectCatalogProductFileThumbnailUrl } from '@/services/project-catalog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarTime, faImage, faShelves } from '@fortawesome/pro-light-svg-icons';

function isSelected(value: OptionGroupOverrideSelection[], optionGroupId: number, roomId: number | null): boolean {
    return value.some((s) => s.optionGroupId === optionGroupId && s.roomId === roomId);
}

function toggleSelection(
    value: OptionGroupOverrideSelection[],
    optionGroupId: number,
    roomId: number | null,
    checked: boolean
): OptionGroupOverrideSelection[] {
    if (checked) {
        return [...value, { optionGroupId, roomId }];
    }
    return value.filter((s) => !(s.optionGroupId === optionGroupId && s.roomId === roomId));
}

interface ChangeRequestOverrideOptionSelectProps {
    residentialUnitId: number;
    value: OptionGroupOverrideSelection[];
    onChange(newValue: OptionGroupOverrideSelection[]): void;
}

const ChangeRequestOverrideOptionSelect: React.FC<ChangeRequestOverrideOptionSelectProps> = ({
    residentialUnitId,
    value,
    onChange,
}) => {
    const projectId = useGetActiveProjectId();
    const [optionGroups] = useAsyncState(
        () =>
            searchOptionGroups({
                projectId: projectId!,
                unitIds: [residentialUnitId],
                publishedStatus: [OptionGroupLinkStatus.Approved],
            }),
        [residentialUnitId],
        null
    );
    const [rooms] = useAsyncState(() => getRooms(projectId!), [projectId], null);

    if (!optionGroups || !rooms) {
        return <LoadingIndicator />;
    }

    if (optionGroups.length === 0) {
        return (
            <div className={classnames('text-missing', theme.options)}>
                <Trans>No option groups to override</Trans>
            </div>
        );
    }

    const roomsById = Object.fromEntries(rooms.map((r) => [r.id, r]));

    const renderRow = (group: OptionGroupDto, roomId: number | null, roomBadge?: React.ReactNode) => {
        const selected = isSelected(value, group.id, roomId);
        const toggle = (checked: boolean) => onChange(toggleSelection(value, group.id, roomId, checked));
        const hasBadges = roomBadge !== undefined || group.tags.length > 0;

        return (
            <List.Item
                key={roomId !== null ? `${group.id}-${roomId}` : group.id}
                interactive
                onClick={() => toggle(!selected)}>
                <Checkbox
                    inputSize="md"
                    aria-label={group.name}
                    checked={selected}
                    onChange={(e) => toggle(e.target.checked)}
                />
                <div className={theme.thumb}>
                    {group.defaultProductImage ? (
                        <img src={getProjectCatalogProductFileThumbnailUrl(group.defaultProductImage.id)} alt="" />
                    ) : (
                        <FontAwesomeIcon icon={faImage} className={theme.thumbPlaceholder} />
                    )}
                </div>
                <List.Content title={<OptionGroupLabel name={group.name} internalNote={group.internalNote} />}>
                    {hasBadges && (
                        <div className={theme.badges}>
                            {roomBadge}
                            {group.tags.map((tag) => (
                                <Badge key={tag.id} size="sm" appearance="subtle">
                                    {tag.label}
                                </Badge>
                            ))}
                        </div>
                    )}
                </List.Content>
                <List.Trailing>
                    <div className={theme.meta}>
                        <span>
                            <FontAwesomeIcon icon={faShelves} />
                            <Plural value={group.optionCount} one="# product" other="# products" />
                        </span>
                        <span>
                            <FontAwesomeIcon icon={faCalendarTime} />
                            {group.deadline.name}
                        </span>
                    </div>
                </List.Trailing>
            </List.Item>
        );
    };

    return (
        <div className={theme.options}>
            <List>
                {optionGroups.flatMap((group) => {
                    if (group.linkType === LinkingType.Room) {
                        return group.linkedRooms.map((link) => {
                            const room = roomsById[link.roomId];
                            const roomBadge = room ? <RoomPill room={room} /> : <span>{`Room ${link.roomId}`}</span>;
                            return renderRow(group, link.roomId, roomBadge);
                        });
                    }
                    return renderRow(group, null);
                })}
            </List>
        </div>
    );
};

export default ChangeRequestOverrideOptionSelect;
